<script lang="ts">
  import { getModalStore, getToastStore, ProgressRadial } from '@skeletonlabs/skeleton'
  import { onMount, type SvelteComponent } from 'svelte'

  import ClassMappingEditor from './ClassMappingEditor.svelte'
  import {
    displayModelSourceTypes,
    type ICreateModel,
    type IModel,
    isPyTorchRawPthSource,
    isTorchVisionSource,
    ModelSourceType,
    modelSourceTypesValues,
    PyTorchRawPthSource,
    TorchVisionSource,
  } from './models-store.svelte'
  import { runUnlearning } from '../runs/unlearning-runs'
  import CloseModalButton from '@components/CloseModalButton.svelte'
  import InvalidHoverInfo from '@components/InvalidHoverInfo.svelte'
  import InvalidIcon from '@components/InvalidIcon.svelte'
  import MetadataFields from '@components/MetadataFields.svelte'
  import ReadOnly from '@components/ReadOnly.svelte'
  import RequiredNameField from '@components/RequiredNameField.svelte'
  import Select from '@components/Select.svelte'
  import StorageConfigurator from '@components/StorageConfigurator.svelte'
  import {
    organizationAsyncState,
    organizationsStore,
  } from '@routes/organizations/organizations-store'
  import {
    createStorageConfig,
    deleteStorageConfig,
    getStoragePrefix,
    type IStorageConfig,
    prepareInputValuesForQuery,
    type StorageType,
  } from '@routes/storage-configs/storage-configs-store'
  import { localStoragePrefixes } from '@stores/local-storage.svelte'
  import { loggedInStore, User } from '@stores/logged-in-store'
  import { type Config, getConfig } from '@utils/config.svelte'
  import { setRestoreState } from '@utils/modal-restore'
  import {
    TORCHVISION_MODEL_NAME_PATTERN,
    torchvisionModelNameValidator,
    ValidityState,
  } from '@utils/validation.svelte'

  import Icon from '$lib/components/Icon.svelte'

  interface Props {
    /** Exposes parent props to this component. */
    parent: SvelteComponent
  }

  let { parent }: Props = $props()

  const modalStore = getModalStore()
  const toastStore = getToastStore()
  const restoreKey: string | undefined = $modalStore[0]?.meta?.restoreKey

  interface ModelModalRestoreState {
    modelInputValues: ModelInput
    isAccordionOpen: boolean
    selectedStorageType: StorageType | null
    storageConfigName: string
    storageInputValues: Record<string, unknown>
  }
  const isModelModalRestoreState = (v: unknown): v is ModelModalRestoreState =>
    !!v && typeof v === 'object' && 'modelInputValues' in (v as Record<string, unknown>)

  let config: Config | undefined = $state()

  const currentUser: User = $loggedInStore

  let model: IModel | null = $modalStore[0].value
  const action = $derived(model && !$modalStore[0].meta?.duplicate ? 'update' : 'create')
  let currentOrganizationId = model?.organizationId ?? currentUser.primaryOrganizationId
  let selectedStorageConfig: IStorageConfig | null = $state(null) // For selected storage in search input
  let modelFormElement: HTMLFormElement | undefined = $state()

  const readOnly = $modalStore[0]?.meta?.readOnly || false

  type ModelInput = Partial<Pick<IModel, 'organizationId'>> &
    Pick<IModel, 'classMappingUrl' | 'dataRootUrl' | 'modelName' | 'modelSource'> & {
      classMappingJson?: IModel['classMappingJson']
      selectedStorageConfigId?: number | null
      selectedStorageConfigName: string
    }

  let modelInputValues: ModelInput = $state({
    classMappingUrl: null,
    classMappingJson: null,
    dataRootUrl: '',
    organizationId: currentOrganizationId,
    selectedStorageConfigName: '',
    modelName: '',
    modelSource: new TorchVisionSource(),
  })
  let invalids = $state({
    classMappingUrl: new ValidityState(),
    classMappingJson: new ValidityState(),
    dataRootUrl: new ValidityState(),
    selectedStorageConfigName: new ValidityState(),
    modelName: new ValidityState(),
    csvUrl: new ValidityState(),
    safetensorsWeightsUrl: new ValidityState(),
    rawPthUrl: new ValidityState(),
    codeUrl: new ValidityState(),
    pytorchModelName: new ValidityState(),
  })

  let isAccordionOpen = $state(false)
  let isEditMode = $state(false)
  let isLoading = $state(false)

  // Values for the storageFormFields component
  let storageFormElement: HTMLFormElement | undefined = $state()
  const initialStorageConfig = $state.snapshot(model?.storageConfig)
  let storageConfig = $state(initialStorageConfig)
  let selectedStorageType = $state(initialStorageConfig?.type || null)
  let storageConfigName = $state(initialStorageConfig?.name || '')
  let storageInputValues = $state({}) // TODO: type more strictly

  type ClassMappingMode = 'url' | 'json'
  let classMappingMode: ClassMappingMode = $state('json')
  let classMappingEditorRef: ClassMappingEditor | null = $state(null)

  onMount(async () => {
    await initializeInputValues(model)
    config = await getConfig(toastStore)

    // Restore previously cached state if present
    if (
      $modalStore[0]?.meta?.restoreState &&
      isModelModalRestoreState($modalStore[0].meta.restoreState)
    ) {
      const restoreState = $modalStore[0].meta.restoreState
      modelInputValues = restoreState.modelInputValues
      isAccordionOpen = restoreState.isAccordionOpen
      selectedStorageType = restoreState.selectedStorageType
      storageConfigName = restoreState.storageConfigName
      storageInputValues = restoreState.storageInputValues
    }
  })

  $effect(() => {
    if (!restoreKey) {
      return
    }
    setRestoreState(restoreKey, {
      modelInputValues,
      isAccordionOpen,
      selectedStorageType,
      storageConfigName,
      storageInputValues,
    } satisfies ModelModalRestoreState)
  })

  const initializeInputValues = async (model: IModel | null) => {
    if (!model) {
      return
    }

    selectedStorageConfig = model.storageConfig
    isEditMode = !$modalStore[0].meta?.duplicate // Disables the accordion and search input unless duplicating

    const {
      classMappingUrl,
      classMappingJson,
      dataRootUrl,
      organizationId,
      modelName,
      modelSource,
    } = model

    const newName = $modalStore[0].meta.newName

    modelInputValues = {
      classMappingUrl,
      dataRootUrl,
      classMappingJson,
      organizationId,
      selectedStorageConfigId: selectedStorageConfig?.id,
      selectedStorageConfigName: selectedStorageConfig?.name,
      modelName: newName ? newName : modelName,
      modelSource,
    }
    classMappingMode =
      classMappingJson && Object.keys(classMappingJson || {}).length ? 'json' : 'url'
  }

  const submitStorageConfigFields = async () => {
    if (!storageFormElement?.reportValidity()) {
      return
    }
    if (!selectedStorageType) {
      console.error('No storage type selected')
      return
    }

    const storageConfigExtra = prepareInputValuesForQuery(storageInputValues, selectedStorageType)
    const storageConfigPayload = {
      ...storageConfigExtra,
      organizationId: currentOrganizationId,
      name: storageConfigName,
      type: selectedStorageType,
    }

    const newStorageConfig = await createStorageConfig(
      storageConfigPayload,
      toastStore,
      currentUser.userId,
      currentUser.fullName,
    )
    if (!newStorageConfig) {
      console.error('Failed to create new storage config')
      return
    }
    return newStorageConfig
  }

  const createModel = async (): Promise<ICreateModel> => {
    let createdStorageConfig = false
    try {
      if (isAccordionOpen) {
        const storageConfigCreated = await submitStorageConfigFields()
        if (storageConfigCreated) {
          createdStorageConfig = true
        } else {
          console.error(`Failed to ${action} new storage config from model form`)
          throw new Error(`Failed to ${action} new storage config from model form`)
        }
        modelInputValues.selectedStorageConfigId = storageConfigCreated.id
      }
      if (!modelInputValues.selectedStorageConfigId) {
        console.error('No storage config selected')
        throw new Error('No storage config selected')
      }

      const {
        selectedStorageConfigId: storageConfigId,
        classMappingUrl,
        classMappingJson,
        dataRootUrl,
        organizationId,
        modelName,
        modelSource,
      } = modelInputValues
      const payload = {
        storageConfigId,
        classMappingUrl: classMappingMode === 'url' ? classMappingUrl : null,
        classMappingJson: classMappingMode === 'json' ? classMappingJson : null,
        dataRootUrl,
        organizationId,
        modelName,
        modelSource,
      }

      if (!$modalStore[0].response) {
        console.error("Modal doesn't have a response function")
        throw new Error("Modal doesn't have a response function")
      }
      await $modalStore[0].response({
        payload: {
          storageConfigId: modelInputValues.selectedStorageConfigId,
          classMappingUrl: classMappingMode === 'url' ? classMappingUrl : null,
          classMappingJson: classMappingMode === 'json' ? classMappingJson : null,
          dataRootUrl,
          organizationId,
          modelName,
          modelSource: $state.snapshot(modelSource), // Serialize class properly
        },
      })
      return payload
    } catch (e) {
      if (createdStorageConfig && modelInputValues.selectedStorageConfigId) {
        await deleteStorageConfig(modelInputValues.selectedStorageConfigId)
        // ⬆️ This cleans up any storage config that was created, enabling the user to retry
      }
      console.error(`Failed to ${action} model`)
      throw e
    }
  }

  // Validate class mapping if in JSON mode and return true if valid, false otherwise
  const validateClassMapping = (): boolean => {
    if (classMappingMode === 'json' && classMappingEditorRef) {
      const validation = classMappingEditorRef.validate()
      if (!validation.valid) {
        invalids.classMappingJson.setInvalid(validation.error || 'Invalid class mapping')
        toastStore.trigger({
          message: validation.error || 'Invalid class mapping',
          background: 'variant-filled-error',
        })
        return false
      }
      invalids.classMappingJson.setValid()
    }
    return true
  }

  const createHandler = async () => {
    if (!modelFormElement?.reportValidity()) {
      return
    }
    if (!validateClassMapping()) {
      return
    }
    try {
      isLoading = true
      await createModel()
      parent.onClose()
    } finally {
      isLoading = false
    }
  }

  const createAndRunHandler = async () => {
    if (!modelFormElement?.reportValidity()) {
      return
    }
    if (!validateClassMapping()) {
      return
    }
    try {
      isLoading = true
      const model = await createModel()
      const modelId = $modalStore[0].meta.modelId
      if (!modelId) {
        console.error(`Failed to ${action} model. Could not run unlearning`)
        return
      }
      await runUnlearning(model, modelId, toastStore, modalStore, modelInputValues.modelName)
      parent.onClose()
    } finally {
      isLoading = false
    }
  }

  const dataPrefix = $derived.by(() =>
    getStoragePrefix({
      storageInputValues,
      selectedStorageConfig,
      type: selectedStorageType ?? selectedStorageConfig?.type,
      localStoragePrefix: localStoragePrefixes.datasetUrlPrefix,
    }),
  )

  const modelPrefix = $derived.by(() =>
    getStoragePrefix({
      storageInputValues,
      selectedStorageConfig,
      type: selectedStorageType ?? selectedStorageConfig?.type,
      localStoragePrefix: localStoragePrefixes.modelCodeUrlPrefix,
    }),
  )
</script>

{#snippet invalidIcon(field: keyof typeof invalids, value?: string | null)}
  <InvalidIcon {invalids} {field} {value}></InvalidIcon>
{/snippet}
{#snippet invalidHoverInfo(field: keyof typeof invalids)}
  <InvalidHoverInfo {invalids} {field}></InvalidHoverInfo>
{/snippet}

{#if $modalStore[0]}
  <form
    class="card relative m-3 h-[30rem] max-h-[90vh] w-[48rem] max-w-[80vw] justify-self-center overflow-y-scroll px-6"
    bind:this={modelFormElement}
    class:dimmed={isLoading}
  >
    {#if isLoading}
      <div
        class="fixed left-0 top-0 z-50 flex h-full w-full items-center justify-center bg-secondary-400 bg-opacity-20"
      >
        <ProgressRadial
          stroke={75}
          meter="stroke-surface-200"
          track="stroke-surface-200/30"
          strokeLinecap="round"
          indeterminate={true}
        />
      </div>
    {/if}
    <h3 class="h3 sticky top-0 z-10 justify-between bg-surface-50 py-4 dark:bg-surface-800">
      {$modalStore[0].title}
      <CloseModalButton onClose={parent.onClose} />
    </h3>
    <div class="m-1">{$modalStore[0].body}</div>

    <RequiredNameField
      id="modelName"
      label="Model name:"
      {invalids}
      field="modelName"
      bind:value={modelInputValues.modelName}
      {readOnly}
    />

    <label for="organization-to-associate-with">Associated organization:</label>
    {#if readOnly}
      <ReadOnly
        value={modelInputValues.organizationId
          ? $organizationsStore.get(modelInputValues.organizationId)?.name
          : ''}
      />
    {:else if $organizationAsyncState.loading}
      <p>Loading organizations...</p>
    {:else if $organizationAsyncState.error}
      <p>{$organizationAsyncState.error}</p>
    {:else}
      <Select
        required
        id="organization-to-associate-with"
        name="organization-to-associate-with"
        bind:value={modelInputValues.organizationId}
      >
        <option selected value>Select organization</option>
        {#each $organizationsStore as [_id, organization] (organization.id)}
          <option
            value={organization.id}
            selected={organization.id === modelInputValues.organizationId}
          >
            {organization.name}
          </option>
        {/each}
      </Select>
    {/if}

    <StorageConfigurator
      {readOnly}
      bind:isEditMode
      bind:invalids
      bind:selectedStorageConfig
      bind:isAccordionOpen
      bind:inputValues={modelInputValues}
      bind:storageInputValues
      bind:storageFormElement
      bind:storageConfigName
      bind:selectedStorageType
      bind:storageConfig
      creating="model"
    />

    {#if config?.airGapped}
      <label for="metadataType">Model source:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.type} />
      {:else}
        <Select
          name="metadataType"
          placeholder="Select Type"
          required
          value={modelInputValues.modelSource.type}
          onchange={(e) => {
            switch (e.currentTarget.value) {
              case ModelSourceType.PYTORCH_RAW_PTH:
                modelInputValues.modelSource = new PyTorchRawPthSource()
                break
              case ModelSourceType.TORCHVISION:
                modelInputValues.modelSource = new TorchVisionSource()
                break
            }
          }}
        >
          <option selected value>Select type</option>
          {#each modelSourceTypesValues as modelSourceType (modelSourceType)}
            <option value={modelSourceType}>{displayModelSourceTypes[modelSourceType]}</option>
          {/each}
        </Select>
      {/if}
    {/if}

    {#if isTorchVisionSource(modelInputValues.modelSource)}
      <label for="pytorchModelName">PyTorch model name:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.modelName} />
      {:else}
        <div class="input-group input-group-divider grid-cols-[1fr_auto]">
          <input
            class="autocomplete my-0 box-border flex px-2 py-1 md:min-w-96"
            type="text"
            id="pytorchModelName"
            value={modelInputValues.modelSource.modelName}
            oninvalid={torchvisionModelNameValidator(invalids.pytorchModelName)}
            onchange={(e) => {
              torchvisionModelNameValidator(invalids.pytorchModelName)
              if (isTorchVisionSource(modelInputValues.modelSource)) {
                modelInputValues.modelSource.modelName = e.currentTarget.value
              }
            }}
            required
            pattern={TORCHVISION_MODEL_NAME_PATTERN.source}
          />
          {@render invalidIcon('modelName', modelInputValues.modelSource.modelName)}
        </div>
        {@render invalidHoverInfo('modelName')}
      {/if}

      <label for="safetensorsWeightsUrl">Safetensors weights URL:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.safetensorsWeightsUrl} />
      {:else}
        <MetadataFields
          prefix={modelPrefix}
          {invalids}
          fieldName="safetensorsWeightsUrl"
          fieldValue={modelInputValues.modelSource.safetensorsWeightsUrl}
          setField={(newValue) => {
            if (!isTorchVisionSource(modelInputValues.modelSource)) {
              console.error('Model source is not TorchVision')
              return
            }
            modelInputValues.modelSource.safetensorsWeightsUrl = newValue
            if (newValue.replace(modelPrefix, '')) {
              invalids.safetensorsWeightsUrl.setValid()
            } else {
              invalids.safetensorsWeightsUrl.setInvalid('Safetensors URL is required')
            }
          }}
        />
      {/if}
    {:else if isPyTorchRawPthSource(modelInputValues.modelSource)}
      <label for="rawPthUrl">Raw PTH URL:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.rawPthUrl} />
      {:else}
        <MetadataFields
          prefix={modelPrefix}
          {invalids}
          fieldName="rawPthUrl"
          fieldValue={modelInputValues.modelSource.rawPthUrl}
          setField={(newValue) => {
            if (!isPyTorchRawPthSource(modelInputValues.modelSource)) {
              console.error('Model source is not PyTorchRawPth')
              return
            }
            modelInputValues.modelSource.rawPthUrl = newValue
            if (newValue.replace(modelPrefix, '')) {
              invalids.rawPthUrl.setValid()
            } else {
              invalids.rawPthUrl.setInvalid('Raw PTH URL is required')
            }
          }}
        />
      {/if}

      <label for="codeUrl">Code URL:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.modelSource.codeUrl} />
      {:else}
        <MetadataFields
          prefix={modelPrefix}
          {invalids}
          fieldName="codeUrl"
          fieldValue={modelInputValues.modelSource.codeUrl}
          setField={(newValue) => {
            if (!isPyTorchRawPthSource(modelInputValues.modelSource)) {
              console.error('Model source is not PyTorchRawPth')
              return
            }
            modelInputValues.modelSource.codeUrl = newValue
            if (newValue.replace(modelPrefix, '')) {
              invalids.codeUrl.setValid()
            } else {
              invalids.codeUrl.setInvalid('Code URL is required')
            }
          }}
        />
      {/if}
    {/if}

    <p>Class mapping source:</p>
    <div class="flex gap-4">
      <label class="flex items-center gap-2">
        <input type="radio" name="classMappingMode" value="url" bind:group={classMappingMode} />
        URL
      </label>
      <label class="flex items-center gap-2">
        <input type="radio" name="classMappingMode" value="json" bind:group={classMappingMode} />
        Inline JSON
      </label>
    </div>

    {#if classMappingMode === 'url'}
      <label for="classMappingUrl">Class mapping URL:</label>
      {#if readOnly}
        <ReadOnly value={modelInputValues.classMappingUrl} />
      {:else}
        <MetadataFields
          prefix={dataPrefix}
          {invalids}
          fieldName="classMappingUrl"
          fieldValue={modelInputValues.classMappingUrl ?? ''}
          setField={(newValue) => {
            modelInputValues.classMappingUrl = newValue
            if ((newValue ?? '').replace(dataPrefix, '')) {
              invalids.classMappingUrl.setValid()
            } else {
              invalids.classMappingUrl.setInvalid('Class mapping URL is required')
            }
          }}
        />
      {/if}
    {:else}
      <ClassMappingEditor
        bind:this={classMappingEditorRef}
        bind:value={modelInputValues.classMappingJson}
        {readOnly}
      />
    {/if}

    <label for="dataRootUrl">Data root URL:</label>
    {#if readOnly}
      <ReadOnly value={modelInputValues.dataRootUrl} />
    {:else}
      <MetadataFields
        prefix={dataPrefix}
        {invalids}
        fieldName="dataRootUrl"
        fieldValue={modelInputValues.dataRootUrl}
        setField={(newValue) => {
          modelInputValues.dataRootUrl = newValue
          invalids.dataRootUrl.setValid()
        }}
        required={false}
      />
    {/if}

    <footer
      class="modal-footer sticky bottom-0 bg-surface-50 pb-2 pt-2 dark:bg-surface-800 {parent.regionFooter}"
    >
      <div class="flex w-full items-center justify-between">
        {#if !readOnly}
          <div class="btn-group space-x-2">
            <button
              class="variant-filled-primary {parent.buttonPositive}"
              type="button"
              onclick={createHandler}
              disabled={isLoading}
            >
              {parent.buttonTextSubmit}
            </button>
            <button
              class="variant-filled-error"
              type="button"
              onclick={parent.onClose}
              disabled={isLoading}
            >
              {parent.buttonTextCancel}
            </button>
          </div>
        {/if}
        {#if !isEditMode}
          <div class="btn-group">
            <button
              class="variant-filled-primary"
              type="button"
              onclick={createAndRunHandler}
              disabled={isLoading}
            >
              <Icon icon="iconoir:play-solid" />
            </button>
          </div>
        {/if}
      </div>
    </footer>
  </form>
{/if}

<style lang="postcss">
  form {
    flex-direction: column;
  }
  form > :not(:first-child) {
    margin: 0.5rem 0;
  }

  .disable-accordion {
    @apply pointer-events-none cursor-not-allowed opacity-50;
  }
</style>
